import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { X, ArrowRight, ArrowLeft, Sparkles, ShoppingBag, ShoppingCart, Receipt, CheckCircle2 } from 'lucide-react'

const SLIDES = [
  {
    icon: Sparkles,
    color: '#4F46E5',
    tag: 'Welcome',
    title: 'Sell smarter, grow faster',
    desc: 'Tamaro admin panel ready che. Products, orders ane payments, badhu ek j jagya e manage karo.',
    points: ['Real-time dashboard', 'Easy product management', 'Fast payment tracking'],
  },
  {
    icon: ShoppingBag,
    color: '#06B6D4',
    tag: 'Step 1',
    title: 'Add your products',
    desc: 'Categories banavo ane products upload karo. Price, stock ane status kyarey pan edit kari sakay.',
    points: ['Category wise catalog', 'Stock & price control', 'Active / inactive toggle'],
    path: '/product',
    cta: 'Add Products',
  },
  {
    icon: ShoppingCart,
    color: '#10B981',
    tag: 'Step 2',
    title: 'Manage your orders',
    desc: 'Navi orders turant dekhay. Order accept karo, pack karo ane status update karo.',
    points: ['New order alerts', 'Track order status', 'Customer details at a glance'],
    path: '/order',
    cta: 'View Orders',
  },
  {
    icon: Receipt,
    color: '#F59E0B',
    tag: 'Step 3',
    title: 'Get paid on time',
    desc: 'Badhi transactions ane invoices ek jagya e. Payment status live check karo.',
    points: ['Transaction history', 'Auto invoices', 'Payment status tracking'],
    path: '/transaction',
    cta: 'View Payments',
  },
]

export default function WelcomeModal() {
  const navigate = useNavigate()
  const admin = useSelector((state) => state.auth?.admin)
  const [visible, setVisible] = useState(false)
  const [closing, setClosing] = useState(false)
  const [step, setStep] = useState(0)

  // session ma ek j var batavo
  useEffect(() => {
    if (sessionStorage.getItem('welcomeShown')) return
    const t = setTimeout(() => {
      setVisible(true)
      sessionStorage.setItem('welcomeShown', 'true')
    }, 600)
    return () => clearTimeout(t)
  }, [])

  const handleClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => setVisible(false), 300)
  }, [])

  // Esc dabavo to band
  useEffect(() => {
    if (!visible) return
    const onKey = (e) => e.key === 'Escape' && handleClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, handleClose])

  if (!visible) return null

  const slide = SLIDES[step]
  const Icon = slide.icon
  const isFirst = step === 0
  const isLast = step === SLIDES.length - 1
  const firstName = (admin?.name || 'Admin').split(' ')[0]

  const next = () => (isLast ? handleClose() : setStep((s) => s + 1))
  const prev = () => setStep((s) => Math.max(0, s - 1))
  const goTo = () => {
    handleClose()
    if (slide.path) navigate(slide.path)
  }

  return createPortal(
    <>
      <div className={`wm-backdrop ${closing ? 'out' : ''}`} onClick={handleClose} />

      <div className="wm-wrap">
        <div className={`wm-card ${closing ? 'out' : ''}`} role="dialog" aria-modal="true">
          <button className="wm-close" onClick={handleClose} aria-label="Close"><X size={16} /></button>

          {/* LEFT: illustration panel */}
          <div className="wm-visual" style={{ '--c': slide.color }} key={`v-${step}`}>
            <div className="wm-orb wm-orb-1" />
            <div className="wm-orb wm-orb-2" />
            <div className="wm-icon-ring">
              <div className="wm-icon-box"><Icon size={40} strokeWidth={1.6} color="#fff" /></div>
            </div>
            <div className="wm-visual-tag">{slide.tag}</div>
          </div>

          {/* RIGHT: content */}
          <div className="wm-content">
            <div key={`c-${step}`} className="wm-slide">
              {isFirst && <div className="wm-hello">Hi {firstName} 👋</div>}
              <h2>{slide.title}</h2>
              <p>{slide.desc}</p>

              <ul className="wm-points">
                {slide.points.map((p) => (
                  <li key={p}>
                    <CheckCircle2 size={16} style={{ color: slide.color, flexShrink: 0 }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* dots */}
            <div className="wm-dots">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  className={`wm-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
                  onClick={() => setStep(i)}
                  aria-label={`Step ${i + 1}`}
                />
              ))}
            </div>

            {/* footer */}
            <div className="wm-footer">
              {isFirst ? (
                <button className="wm-skip" onClick={handleClose}>Skip</button>
              ) : (
                <button className="wm-back" onClick={prev}><ArrowLeft size={14} /> Back</button>
              )}

              <div className="wm-actions">
                {slide.path && (
                  <button className="wm-secondary" onClick={goTo}>{slide.cta}</button>
                )}
                <button className="wm-primary" onClick={next}>
                  {isLast ? 'Get Started' : 'Next'} <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .wm-backdrop {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(4, 6, 16, .65);
          backdrop-filter: blur(6px);
          animation: wm-fade-in .3s ease forwards;
        }
        .wm-backdrop.out { animation: wm-fade-out .3s ease forwards; }
        .wm-wrap {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 2001;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          pointer-events: none;
        }
        .wm-card {
          pointer-events: auto;
          position: relative;
          width: 100%; max-width: 760px;
          display: grid; grid-template-columns: 0.9fr 1.1fr;
          background: var(--bg-surface);
          border: 1px solid var(--border-active);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 32px 80px rgba(0,0,0,.5);
          animation: wm-slide-in .4s cubic-bezier(.16,1,.3,1) forwards;
        }
        .wm-card.out { animation: wm-slide-out .3s ease forwards; }

        .wm-close {
          position: absolute; top: 12px; right: 12px; z-index: 5;
          width: 30px; height: 30px; border-radius: 50%;
          border: 1px solid var(--border); background: var(--bg-raised);
          color: var(--text3); cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all .15s;
        }
        .wm-close:hover { color: var(--text1); background: var(--bg-hover); }

        /* visual panel */
        .wm-visual {
          position: relative; overflow: hidden;
          min-height: 380px;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(145deg, color-mix(in srgb, var(--c) 85%, #000) 0%, color-mix(in srgb, var(--c) 45%, #1a1f35) 100%);
          transition: background .4s ease;
        }
        .wm-orb { position: absolute; border-radius: 50%; filter: blur(40px); pointer-events: none; }
        .wm-orb-1 { width: 220px; height: 220px; background: rgba(255,255,255,.18); top: -70px; left: -60px; }
        .wm-orb-2 { width: 180px; height: 180px; background: rgba(255,255,255,.12); bottom: -60px; right: -40px; }
        .wm-icon-ring {
          position: relative;
          width: 150px; height: 150px; border-radius: 50%;
          border: 2px dashed rgba(255,255,255,.35);
          display: flex; align-items: center; justify-content: center;
          animation: wm-pop .5s cubic-bezier(.16,1,.3,1) both, wm-spin 24s linear infinite;
        }
        .wm-icon-box {
          width: 92px; height: 92px; border-radius: 26px;
          background: rgba(255,255,255,.2);
          border: 1px solid rgba(255,255,255,.4);
          backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 12px 32px rgba(0,0,0,.25);
          animation: wm-spin-rev 24s linear infinite;
        }
        .wm-visual-tag {
          position: absolute; left: 18px; bottom: 18px;
          padding: 4px 12px; border-radius: 999px;
          font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
          color: #fff; background: rgba(255,255,255,.2);
          border: 1px solid rgba(255,255,255,.35);
        }

        /* content */
        .wm-content { padding: 2rem 2rem 1.5rem; display: flex; flex-direction: column; min-height: 380px; }
        .wm-slide { flex: 1; animation: wm-swap .35s ease both; }
        .wm-hello {
          display: inline-block; margin-bottom: 10px;
          font-size: 12px; font-weight: 700; color: var(--accent-hover);
          background: var(--accent-glow-sm); border: 1px solid var(--border);
          padding: 3px 10px; border-radius: 999px;
        }
        .wm-content h2 {
          font-size: 1.5rem; font-weight: 800; color: var(--text1);
          letter-spacing: -.025em; line-height: 1.2; margin-bottom: 8px;
        }
        .wm-content p { font-size: var(--fs-base); color: var(--text2); line-height: 1.65; }
        .wm-points { list-style: none; margin-top: 18px; display: flex; flex-direction: column; gap: 10px; }
        .wm-points li {
          display: flex; align-items: center; gap: 10px;
          font-size: var(--fs-base); color: var(--text1); font-weight: 500;
          padding: 9px 12px; border-radius: 10px;
          background: var(--bg-raised); border: 1px solid var(--border-subtle);
        }

        .wm-dots { display: flex; gap: 6px; margin: 18px 0 16px; }
        .wm-dot {
          height: 6px; width: 6px; border-radius: 999px; border: none; cursor: pointer;
          background: var(--text4); transition: all .25s ease; padding: 0;
        }
        .wm-dot.done { background: var(--accent-hover); opacity: .6; }
        .wm-dot.active { width: 22px; background: var(--accent); }

        .wm-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .wm-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
        .wm-skip, .wm-back {
          background: none; border: none; cursor: pointer;
          font-family: inherit; font-size: 13px; font-weight: 600; color: var(--text3);
          display: inline-flex; align-items: center; gap: 4px; padding: 8px 4px;
        }
        .wm-skip:hover, .wm-back:hover { color: var(--text1); }
        .wm-primary {
          display: inline-flex; align-items: center; gap: 6px;
          background: var(--accent); color: #fff; border: none; border-radius: 8px;
          padding: 9px 20px; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
          box-shadow: 0 4px 14px rgba(79,70,229,.35); transition: all .15s;
        }
        .wm-primary:hover { background: var(--accent-hover); transform: translateY(-1px); }
        .wm-secondary {
          background: transparent; color: var(--accent-hover);
          border: 1px solid var(--border-active); border-radius: 8px;
          padding: 9px 16px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all .15s;
        }
        .wm-secondary:hover { background: var(--bg-hover); }

        @keyframes wm-fade-in  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes wm-fade-out { from { opacity: 1 } to { opacity: 0 } }
        @keyframes wm-slide-in  { from { opacity: 0; transform: translateY(28px) scale(.96) } to { opacity: 1; transform: none } }
        @keyframes wm-slide-out { from { opacity: 1; transform: none } to { opacity: 0; transform: translateY(16px) scale(.97) } }
        @keyframes wm-swap { from { opacity: 0; transform: translateX(14px) } to { opacity: 1; transform: none } }
        @keyframes wm-pop  { from { opacity: 0; transform: scale(.6) } to { opacity: 1; transform: scale(1) } }
        @keyframes wm-spin     { to { transform: rotate(360deg) } }
        @keyframes wm-spin-rev { to { transform: rotate(-360deg) } }

        @media (max-width: 700px) {
          .wm-card { grid-template-columns: 1fr; max-width: 420px; }
          .wm-visual { min-height: 170px; }
          .wm-icon-ring { width: 110px; height: 110px; }
          .wm-icon-box { width: 68px; height: 68px; border-radius: 20px; }
          .wm-content { min-height: 0; padding: 1.25rem; }
          .wm-content h2 { font-size: 1.25rem; }
        }
      `}</style>
    </>,
  document.body
  )
}